/* Scroll velocity — Aborder Products.
 *
 * THE PROBLEM THIS SOLVES
 *
 * Every reveal on this site is an IntersectionObserver that adds a class, plus
 * a CSS transition, plus a per-item stagger. Those numbers were tuned by
 * scrolling at reading speed, and at reading speed they are right.
 *
 * Someone flicking a trackpad through the whole page is a different case
 * entirely. Three things stack up against them:
 *
 *   1. Every observer here uses a NEGATIVE bottom rootMargin (-5% to -8%), so
 *      an element has to be properly inside the viewport before it is even
 *      told to start. At 3000px/s it has travelled a third of a screen in the
 *      time that takes.
 *   2. The staggers are 70-80ms per item, and the theme's own cascade is 75ms
 *      or, on slide-in-text, 200ms per item. The sixth card in a grid is a
 *      second behind the first.
 *   3. Durations run 0.6s to 1.5s.
 *
 * Add those up and a fast scroller sees a page of half-drawn, half-faded
 * content, because they are moving faster than the animation is willing to
 * finish. The animation is not wrong; it is just not built for their speed.
 *
 * WHAT THIS DOES
 *
 * Measures how fast the page is actually moving and lets everything else
 * respond to it. Two mechanisms, one here and one in scroll-velocity.css:
 *
 *   - It stamps `data-scroll-speed="fast"` or `"rush"` on <html>. The
 *     stylesheet keys off that attribute to compress durations and flatten
 *     staggers to nearly nothing. Slow down and the attribute goes away and
 *     the original timings come straight back.
 *
 *   - It runs ahead of the observers. While the page is moving fast it looks
 *     for reveal targets inside a lookahead window sized from the current
 *     velocity — roughly where the reader will be a third of a second from
 *     now — and reveals them immediately, with the stagger zeroed.
 *
 * WHY IT LIVES IN ONE FILE INSTEAD OF IN EACH REVEAL SCRIPT
 *
 * There are six of these reveal systems (blog, collection, homepage, brand
 * showcase, product media, contact form) plus the theme's own scroll-trigger.
 * Threading velocity through all seven would mean seven edits, seven chances
 * to break a working animation, and seven copies of the same scroll maths.
 * The families are a table at the top of this file instead: one place to add
 * a system, and nothing existing has to be touched.
 *
 * FAIL-OPEN
 *
 * This file only ever ADDS a class earlier than it would otherwise arrive, and
 * sets one attribute. If it never loads, never runs, or throws, every reveal
 * behaves exactly as it does today. Nothing is hidden on its behalf, and no
 * other script depends on it existing.
 *
 * All the reveal families below are one-way — their own observers only ever
 * add the class and then unobserve, never remove it — which is what makes it
 * safe to add it early from out here.
 */
(function () {
  'use strict';

  /* Nothing to accelerate: the reveals are already off. */
  if (
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return;
  }

  var root = document.documentElement;

  /* Velocity in px/s. Entering `fast` at 1200 is about a firm trackpad flick;
     `rush` at 3000 is a scrollbar drag or a hard swipe. The exit thresholds
     are lower than the entry ones on purpose — without that gap the attribute
     flickers on and off around the boundary and takes the transition
     durations with it, which is visible. */
  var ENTER_FAST = 1200;
  var ENTER_RUSH = 3000;
  var EXIT_FAST = 700;
  var EXIT_RUSH = 2100;

  /* How far ahead to reveal, expressed as time rather than distance: at any
     speed, roughly where the reader will be a third of a second from now.
     Clamped so it is never a pointless few pixels and never the whole
     document. */
  var LOOKAHEAD_SECONDS = 0.34;
  var LOOKAHEAD_MIN = 240;
  var LOOKAHEAD_MAX = 2600;

  var FLUSH_INTERVAL = 110; /* ms between sweeps while moving fast */
  var IDLE_MS = 150; /* no scroll event for this long means stopped */

  /* Each reveal system on the site. `sel` finds targets that have not been
     revealed yet, `cls` is the class its own observer would eventually add,
     and `delayVar` is the inline custom property carrying its stagger, which
     is zeroed on the way through — a stagger is a nicety at reading speed and
     just latency at speed.

     The theme's own `.scroll-trigger` is deliberately NOT in this table. Its
     observer is two-way: it REMOVES the visible class when an element leaves
     the viewport. Adding that class from out here would race against it. Its
     cascade and duration are compressed in the stylesheet instead, which
     cannot conflict with anything. */
  var FAMILIES = [
    { sel: '.blog-reveal:not(.blog-reveal--in)', cls: 'blog-reveal--in', delayVar: '--blog-reveal-delay' },
    { sel: '.cl-reveal:not(.cl-reveal--in)', cls: 'cl-reveal--in', delayVar: '--cl-reveal-delay' },
    { sel: '.hp-reveal:not(.hp-reveal--in)', cls: 'hp-reveal--in', delayVar: '--hp-reveal-delay' },
    { sel: '.hp-track:not(.hp-track--in)', cls: 'hp-track--in', delayVar: null },
    { sel: '.bs-reveal:not(.bs-reveal--in)', cls: 'bs-reveal--in', delayVar: '--bs-reveal-delay' },
    { sel: '.pdp-media-item:not(.pdp-pop)', cls: 'pdp-pop', delayVar: '--pdp-pop-delay' }
  ];

  var lastY = window.pageYOffset || 0;
  var lastT = 0;
  var velocity = 0; /* smoothed, px/s, always positive */
  var tier = '';
  var ticking = false;
  var lastEventT = 0;
  var lastFlushT = 0;

  function now() {
    return window.performance && window.performance.now
      ? window.performance.now()
      : Date.now();
  }

  function setTier(next) {
    if (next === tier) return;
    tier = next;
    if (next) {
      root.setAttribute('data-scroll-speed', next);
    } else {
      root.removeAttribute('data-scroll-speed');
    }
  }

  /* Hysteresis: the bar to enter a tier is higher than the bar to stay in it. */
  function tierFor(v) {
    if (tier === 'rush') return v >= EXIT_RUSH ? 'rush' : v >= EXIT_FAST ? 'fast' : '';
    if (tier === 'fast') {
      if (v >= ENTER_RUSH) return 'rush';
      return v >= EXIT_FAST ? 'fast' : '';
    }
    if (v >= ENTER_RUSH) return 'rush';
    if (v >= ENTER_FAST) return 'fast';
    return '';
  }

  /* Reveal everything already inside the window the reader is about to cross.
     The window extends in BOTH directions: scrolling up needs the same
     treatment as scrolling down, and anything currently on screen but still
     waiting on a negative rootMargin should stop waiting. */
  function flush(lookahead) {
    var top = -lookahead;
    var bottom = (window.innerHeight || root.clientHeight) + lookahead;

    for (var f = 0; f < FAMILIES.length; f++) {
      var family = FAMILIES[f];
      var pending = document.querySelectorAll(family.sel);
      if (!pending.length) continue;

      for (var i = 0; i < pending.length; i++) {
        var el = pending[i];
        var rect = el.getBoundingClientRect();

        /* Zero-height elements are almost always not laid out yet; revealing
           one would burn its transition while it is still invisible. */
        if (rect.bottom === rect.top) continue;
        if (rect.top > bottom || rect.bottom < top) continue;

        if (family.delayVar) el.style.setProperty(family.delayVar, '0ms');
        el.classList.add(family.cls);
      }
    }
  }

  function frame() {
    ticking = false;

    var t = now();
    var y = window.pageYOffset || root.scrollTop || 0;
    var dt = t - lastT;

    /* A VERY SHORT FRAME IS NOT A MEASUREMENT.

       Dividing by dt means a sub-millisecond gap turns an ordinary 340px jump
       into six figures of px/s. Measured on the harness: readings of 127,000
       and 325,000 px/s, against a real hard flick of perhaps 4,000. The
       lookahead is clamped so nothing visibly broke, but the average then took
       a quarter of a second to fall back out of `rush` after the page had
       already stopped.

       Frames shorter than this are skipped WITHOUT advancing the baseline, so
       the distance is not thrown away — it just gets counted against the next
       interval that is long enough to divide by. */
    if (dt < 4) {
      request();
      return;
    }

    if (dt > 0) {
      /* Clamped as well as gated. Nothing a human does with a trackpad, a
         wheel or a scrollbar exceeds this; anything above it is a measurement
         artefact, and letting it into the average only delays the decay. */
      var instant = Math.min(20000, (Math.abs(y - lastY) / dt) * 1000);

      /* An exponential average rather than the raw reading. A single frame's
         delta is noisy enough — one long frame during layout reads as a
         thousand px/s — that acting on it directly would trip the tier on a
         page that is barely moving.

         Weighted toward the new reading rather than evenly, because this has
         to react inside the first few frames of a flick to be any use: by the
         time a slow average agrees that the page is moving, the reader is
         already past the thing it was meant to reveal early. */
      velocity = velocity * 0.55 + instant * 0.45;
    }

    lastY = y;
    lastT = t;

    setTier(tierFor(velocity));

    if (tier && t - lastFlushT >= FLUSH_INTERVAL) {
      lastFlushT = t;
      var lookahead = Math.max(
        LOOKAHEAD_MIN,
        Math.min(LOOKAHEAD_MAX, velocity * LOOKAHEAD_SECONDS)
      );
      try {
        flush(lookahead);
      } catch (e) {
        /* A failed sweep costs nothing: the observers still fire normally. */
      }
    }

    /* Keep measuring for a moment after the last scroll event so the tier
       decays to nothing instead of being left latched at whatever the final
       reading was. */
    if (t - lastEventT < IDLE_MS || velocity > 1) {
      if (t - lastEventT >= IDLE_MS) velocity *= 0.6;
      request();
    } else {
      velocity = 0;
      setTier('');
    }
  }

  function request() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(frame);
  }

  function onScroll() {
    var t = now();

    /* RE-BASELINE AFTER AN IDLE GAP.

       `lastT` and `lastY` are only refreshed inside the rAF loop, and the loop
       stops when scrolling stops. Come back to a page after ten seconds and
       flick it, and the first measured frame divides a real 400px delta by ten
       seconds of standing still — about 40px/s, which is nothing. Measured on
       the harness: the first two frames of a hard flick read 0 and 20px/s
       before the average caught up, so the tier engaged four frames late,
       which is most of the distance the reveal was supposed to get ahead of.

       Resetting the clock and the position on the first event of a new gesture
       means the very next frame measures that gesture and nothing else. */
    if (t - lastEventT > IDLE_MS) {
      lastT = t;
      lastY = window.pageYOffset || root.scrollTop || 0;
    }

    lastEventT = t;
    request();
  }

  function start() {
    lastT = now();
    lastY = window.pageYOffset || 0;
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  try {
    /* A read-only handle for anything that wants to know, without having to
       measure it a second time. */
    window.AborderScrollSpeed = {
      velocity: function () {
        return velocity;
      },
      tier: function () {
        return tier;
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  } catch (e) {
    /* Every reveal on the site works without this file. */
  }
})();
